import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Avatar, Box, Toolbar, IconButton, Typography, Tooltip, Stack } from '@mui/material';
import { Menu, Phone, WhatsApp } from '@mui/icons-material';

import logo from 'shared/img/logos/cvt.svg';
import marcoImg from 'shared/img/team/marco.webp';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';

export const Nav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setMenuOpen, isMobile } = useContext(LayoutContext);
  const isHome = pathname === '/' || pathname === '/home';

  const [navVisible, setNavVisible] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const lastScrollY = useRef(0);

  // Show nav after home animation completes
  useEffect(() => {
    const handleComplete = () => {
      setIntroComplete(true);
      setNavVisible(true);
    };
    window.addEventListener('homeAnimationComplete', handleComplete);
    return () => window.removeEventListener('homeAnimationComplete', handleComplete);
  }, []);

  useEffect(() => {
    if (!introComplete) return;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = Math.abs(currentY - lastScrollY.current);
      if (delta < 5) return; // ignore tiny scrolls (e.g. horizontal scroll triggering)
      if (currentY < 100) {
        setNavVisible(true);
      } else if (currentY < lastScrollY.current) {
        setNavVisible(true);
      } else {
        setNavVisible(false);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [introComplete]);

  return (
    <AppBar
      color="transparent"
      elevation={0}
      sx={{
        transform: navVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease',
      }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => setMenuOpen((prevState: boolean) => !prevState)} edge="start">
          <Menu/>
        </IconButton>
        <Typography
          variant="h5"
          sx={{
            cursor: 'pointer',
            opacity: isHome ? 0 : 1,
            transform: isHome ? 'translateY(-10px)' : 'translateY(0)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            pointerEvents: isHome ? 'none' : 'auto',
          }}
          onClick={() => navigate('/')}
        >
          <img alt="CVT Logo" src={logo} style={{ height: 40, width: 'auto', marginBottom: -14, marginLeft: 12 }}/>
        </Typography>
        <div style={{ marginLeft: 'auto'}} />
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#2a2a2e',
          borderRadius: 10,
          pl: 1.5,
          pr: 0.5,
          py: 0.5,
          mr: -1.5,
          mt: 1.5,
          boxShadow: '0 2px 16px rgba(0,0,0,0.4)',
          ...isMobile && { mr: -1 },
        }}>
          <Avatar src={marcoImg} alt="Marco" sx={{ width: 36, height: 36, mr: 0.5 }}/>
          {!isMobile && (
            <Stack direction="column">
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', opacity: 0.8, fontSize: 12 }}>
                Marco, CEO, Human
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', opacity: 0.8, fontSize: 10 }}>
                Ready to Talk or Chat Right Now
              </Typography>
            </Stack>
          )}
          <Tooltip title={<Typography variant="body2">Call</Typography>}>
            <IconButton href="tel:+48536951330" size="small" sx={{ color: '#6365FF' }} edge="end">
              <Phone sx={{ width: 18, height: 18 }}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={<Typography variant="body2">Chat on WhatsApp</Typography>}>
            <IconButton
              href="https://wa.me/48536951330"
              target="_blank"
              size="small"
              sx={{ color: '#25D366', ml: -1 }}
            >
              <WhatsApp sx={{ width: 18, height: 18 }}/>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  )
}
